import { base64ToBytes, bytesToBase64 } from "./chatCrypto";
import { ethers } from "ethers";
export async function publicKeyFingerprint(publicKey) {
    const canonical = JSON.stringify({ crv: publicKey.crv, kty: publicKey.kty, x: publicKey.x, y: publicKey.y });
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonical)));
    return bytesToBase64(digest).replace(/=+$/g, "");
}
/** Exact EIP-191 text for a signed direct-key advertisement. */
export function directKeyAdvertisementMessage(ad) {
    return [
        "Dwinity direct message key (v1)",
        `room: ${ad.room.toLowerCase()}`,
        `device: ${ad.deviceId}`,
        `fingerprint: ${ad.fingerprint}`,
        `epoch: ${ad.epoch}`,
        `expires: ${ad.expiresAt}`,
    ].join("\n");
}
export async function verifyDirectKeyAdvertisement(advertisement, expectedRoom) {
    if (advertisement.version !== 1 ||
        advertisement.room.toLowerCase() !== expectedRoom.toLowerCase() ||
        advertisement.expiresAt <= Date.now() ||
        advertisement.fingerprint !== await publicKeyFingerprint(advertisement.publicKey))
        return "invalid";
    if (!advertisement.signature)
        return "unsigned";
    try {
        const { signature, ...unsigned } = advertisement;
        return ethers.utils.verifyMessage(directKeyAdvertisementMessage(unsigned), signature).toLowerCase() === expectedRoom.toLowerCase()
            ? "wallet"
            : "invalid";
    }
    catch {
        return "invalid";
    }
}
export function safetyConfirmationSurvivesEpoch(previousEpoch, nextEpoch) {
    return previousEpoch === nextEpoch;
}
/** A six-word-ish, 5-group safety number; compare it out of band. */
export async function deriveSafetyNumber(ownPrivateKey, peerPublicJwk, room, ownDeviceId, peerDeviceId, ownFingerprint, peerFingerprint) {
    const peer = await crypto.subtle.importKey("jwk", peerPublicJwk, { name: "ECDH", namedCurve: "P-256" }, false, []);
    const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: peer }, ownPrivateKey, 256);
    const orderedDevices = [ownDeviceId, peerDeviceId].sort().join("|");
    const orderedFingerprints = [ownFingerprint, peerFingerprint].sort().join("|");
    const data = new TextEncoder().encode(`dw-sas-v1|${room.toLowerCase()}|${orderedDevices}|${orderedFingerprints}`);
    const merged = new Uint8Array(bits.byteLength + data.byteLength);
    merged.set(new Uint8Array(bits));
    merged.set(data, bits.byteLength);
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", merged));
    return Array.from(digest.slice(0, 10), (b) => (b % 10000).toString().padStart(4, "0")).join("-");
}
function canonicalBinding(binding) {
    return JSON.stringify([
        "dwinity-secret-message-v1",
        binding.room.toLowerCase(),
        binding.senderDeviceId,
        binding.recipientDeviceId,
        binding.id,
        binding.timestamp,
    ]);
}
export async function generateSecretDeviceKeyPair() {
    const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]);
    return {
        privateKey: pair.privateKey,
        publicJwk: await crypto.subtle.exportKey("jwk", pair.publicKey),
    };
}
async function deriveSecretKey(ownPrivateKey, peerPublicJwk, binding) {
    const peer = await crypto.subtle.importKey("jwk", peerPublicJwk, { name: "ECDH", namedCurve: "P-256" }, false, []);
    const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: peer }, ownPrivateKey, 256);
    const keyMaterial = await crypto.subtle.importKey("raw", bits, "HKDF", false, ["deriveKey"]);
    return crypto.subtle.deriveKey({
        name: "HKDF",
        hash: "SHA-256",
        salt: new TextEncoder().encode("dwinity-secret-message-v1"),
        info: new TextEncoder().encode(canonicalBinding(binding)),
    }, keyMaterial, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}
export async function encryptSecretMessage(plaintext, ownPrivateKey, peerPublicJwk, binding) {
    const key = await deriveSecretKey(ownPrivateKey, peerPublicJwk, binding);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const aad = new TextEncoder().encode(canonicalBinding(binding));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv, additionalData: aad }, key, new TextEncoder().encode(plaintext));
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);
    return `sm1:${bytesToBase64(combined)}`;
}
export async function decryptSecretMessage(payload, ownPrivateKey, peerPublicJwk, binding) {
    if (!payload.startsWith("sm1:"))
        throw new Error("Unsupported secret message envelope");
    const combined = base64ToBytes(payload.slice(4));
    if (combined.length < 29)
        throw new Error("Invalid secret message ciphertext");
    const key = await deriveSecretKey(ownPrivateKey, peerPublicJwk, binding);
    const plaintext = await crypto.subtle.decrypt({
        name: "AES-GCM",
        iv: combined.slice(0, 12),
        additionalData: new TextEncoder().encode(canonicalBinding(binding)),
    }, key, combined.slice(12));
    return new TextDecoder().decode(plaintext);
}
